import { getCSRFToken } from '../utils/getCSRFToken';
import { formatDateISO } from '../utils/dateUtils';
import {
  getPartnerBadgeStyle,
  getPartnerIconUrl,
  getPartnerInitial,
  partnerIconNeedsContainFit,
} from '../utils/partnerIcons';
import {
  safeJsonParse,
  checkAuthError,
  handleAuthError,
} from '../utils/authErrorHandler';

export default function registerCalendarStore() {
  const store = Alpine.store('calendarStore', {
    calendar_selected: [],
    userPermission: null,
    currentDate: null, // This remembers the dates during navigation on the frontend
    dates: [],
    calendar_dates: [],
    accommodations: [],
    tours: [],
    bookingItems: [],
    blockItems: [],
    calendarDatesWithRates: {},
    calendarDatesBooked: {},
    calendarDatesBlocked: {},
    productDatesRateSetBlocked: {},
    productDatesRateSetBlockedNames: {},
    showRatesetBlockedBadge: false,
    showPartnerOnCalendarBookingItems: false,
    isLoading: false,
    isFetching: false,
    showSpeechBubble: false,
    error: null,
    toastStore: Alpine.store('toastStore'),
    bookingSocket: null,
    reconnectAttempts: 0,
    pingInterval: null,
    flatpickrInstance: null,
    isNavigatingDates: false,
    skipNextWebSocketUpdate: false,
    collapsedProducts: {}, // Track collapsed state for each product
    productOccupationSummary: {}, // OPTIMIZED: Precomputed occupation data from backend
    // Cache for filtered items (invalidated when bookingItems/blockItems change)
    _bookingItemsCache: null,
    _blockItemsCache: null,
    _lastBookingItemsLength: 0,
    _lastBlockItemsLength: 0,
    _formShellsPreloadedForCalendarId: null,
    _formShellPreloadTimer: null,
    // Calendar view mode - 10 or 25 days
    daysView: 25, // Default to 25-day view

    // Computed day width percentage for grid cells
    get dayWidthPercent() {
      return 100 / this.daysView;
    },

    // Date utility functions - timezone-safe date parsing
    parseLocalDate(dateStr) {
      if (!dateStr) return null;
      const parts = dateStr.split('-');
      if (parts.length !== 3) return null;
      const year = parseInt(parts[0], 10);
      const month = parseInt(parts[1], 10) - 1; // Month is 0-indexed
      const day = parseInt(parts[2], 10);
      return new Date(year, month, day); // Creates date in local timezone
    },

    getLocalDayOfMonth(dateStr) {
      const date = this.parseLocalDate(dateStr);
      return date ? date.getDate() : 1;
    },

    getLocalMonthShort(dateStr) {
      const date = this.parseLocalDate(dateStr);
      return date
        ? date.toLocaleDateString('en-US', { month: 'short' }).toUpperCase()
        : '';
    },

    getLocalWeekdayShort(dateStr) {
      const date = this.parseLocalDate(dateStr);
      return date ? date.toLocaleDateString('en-US', { weekday: 'short' }) : '';
    },

    isToday(dateStr) {
      const today = new Date();
      const todayStr =
        today.getFullYear() +
        '-' +
        String(today.getMonth() + 1).padStart(2, '0') +
        '-' +
        String(today.getDate()).padStart(2, '0');
      return dateStr === todayStr;
    },

    isWeekend(dateStr) {
      const date = this.parseLocalDate(dateStr);
      if (!date) return false;
      const dayOfWeek = date.getDay();
      return dayOfWeek === 0 || dayOfWeek === 6; // Sunday or Saturday
    },

    formatLocalDate(dateStr) {
      const date = this.parseLocalDate(dateStr);
      return date
        ? date.toLocaleDateString('en-US', {
            weekday: 'short',
            month: 'long',
            day: 'numeric',
            year: 'numeric',
          })
        : '';
    },

    // OPTIMIZATION: Filter booking items by unit_id with memoization
    // This avoids Alpine evaluating bindings for all items on all units
    // Cache is invalidated when bookingItems array changes
    getBookingItemsForUnit(unitId) {
      // Invalidate cache if bookingItems changed
      if (
        this._lastBookingItemsLength !== this.bookingItems.length ||
        !this._bookingItemsCache
      ) {
        this._bookingItemsCache = {};
        this._lastBookingItemsLength = this.bookingItems.length;
      }

      // Return cached result if available
      if (this._bookingItemsCache[unitId]) {
        return this._bookingItemsCache[unitId];
      }

      // Filter and cache
      const filtered = this.bookingItems.filter(
        (item) =>
          item.item?.accommodation_unit === unitId ||
          item.item?.tour_unit === unitId,
      );
      this._bookingItemsCache[unitId] = filtered;
      return filtered;
    },

    getItemFlagColors(item) {
      if (Array.isArray(item?.flag_colors) && item.flag_colors.length) {
        return item.flag_colors;
      }
      return item?.flag_color ? [item.flag_color] : [];
    },

    // OPTIMIZATION: Filter block items by unit_id with memoization
    getBlockItemsForUnit(unitId) {
      // Invalidate cache if blockItems changed
      if (
        this._lastBlockItemsLength !== this.blockItems.length ||
        !this._blockItemsCache
      ) {
        this._blockItemsCache = {};
        this._lastBlockItemsLength = this.blockItems.length;
      }

      // Return cached result if available
      if (this._blockItemsCache[unitId]) {
        return this._blockItemsCache[unitId];
      }

      // Filter and cache
      const filtered = this.blockItems.filter(
        (item) =>
          item.item?.accommodation_unit === unitId ||
          item.item?.tour_unit === unitId,
      );
      this._blockItemsCache[unitId] = filtered;
      return filtered;
    },

    init() {
      console.log('CalendarStore initialized');
      // Initialize properties
      this.bookingSocket = null;
      this.pingInterval = null;
      this.reconnectAttempts = 0;
      this.flatpickrInstance = null;

      // Load days view preference from localStorage
      this.loadDaysViewPreference();

      // Single cleanup listener using Alpine's lifecycle
      document.addEventListener('alpine:cleanup', () => {
        if (this.bookingSocket || this.pingInterval) {
          this.cleanup();
        }
      });

      const calendarContainer = document.getElementById('calendarContainer');
      if (
        calendarContainer &&
        calendarContainer.getAttribute('data-user-permission')
      ) {
        this.userPermission = calendarContainer.getAttribute(
          'data-user-permission',
        );
        console.log('userPermission:', this.userPermission);
      }

      window.addEventListener('beforeunload', () => {
        this.cleanup();
      });
    },

    getDates() {
      try {
        const calendarContainer = document.getElementById('calendarContainer');
        if (
          calendarContainer &&
          calendarContainer.getAttribute('data-initial-dates') &&
          calendarContainer.getAttribute('data-selected-date')
        ) {
          this.dates = JSON.parse(
            calendarContainer.getAttribute('data-initial-dates'),
          );
          this.currentDate = JSON.parse(
            calendarContainer.getAttribute('data-selected-date'),
          );
          //console.log('dates1:', this.dates);
          //console.log('selected_date:', this.currentDate);
        } else {
          console.error(
            'No initial dates found or calendarContainer is missing',
          );
          this.dates = []; // Set default or empty array
        }
      } catch (error) {
        console.error('Error parsing dates:', error);
        this.error = 'Failed to load initial dates';
      }
    },
    // Switch between 10-day and 25-day view
    async setDaysView(days) {
      if (days !== 10 && days !== 25) {
        console.error('Invalid days view. Must be 10 or 25.');
        return;
      }
      if (this.daysView === days) return; // No change needed

      try {
        // Save preference to server
        const response = await fetch('/calendar/update-days-view/', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': getCSRFToken(),
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify({ days_view: days }),
        });

        if (response.ok) {
          // Refresh the page to apply the new view cleanly
          // This avoids visual glitches during the transition
          window.location.reload();
        } else {
          console.error('Failed to save days view preference');
          // Fallback: still update locally and reload data
          this.daysView = days;
          this.fetchGridData();
        }
      } catch (error) {
        console.error('Error saving days view preference:', error);
        // Fallback: still update locally and reload data
        this.daysView = days;
        this.fetchGridData();
      }
    },

    // Load days view preference from server (called during init)
    // The initial value is set from the data attribute on the calendar container
    loadDaysViewPreference() {
      const calendarContainer = document.getElementById('calendarContainer');
      if (calendarContainer) {
        const savedDaysView = calendarContainer.getAttribute('data-days-view');
        if (savedDaysView === '10' || savedDaysView === '25') {
          this.daysView = parseInt(savedDaysView, 10);
        }
      }
    },

    async fetchItemsData() {
      this.isLoading = true;
      const params = new URLSearchParams();
      if (this.currentDate) {
        params.append('date', this.currentDate);
      }
      params.append('days_view', this.daysView);
      const url = `/calendar/load-calendar-items/?${params.toString()}`;
      try {
        const response = await fetch(url, {
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': getCSRFToken(),
            'X-Requested-With': 'XMLHttpRequest',
          },
        });

        // Use safe JSON parsing to handle auth errors gracefully
        const data = await safeJsonParse(response);
        this.calendar_selected = data.calendar_selected;
        this.dates = data.dates;
        this.calendar_dates = data.calendar_dates;
        // calendarDatesBlocked is now loaded from fetchGridData, not here

        this.applyCalendarItemsData(data);

        // Set showTour based on the existence of accommodations and tours
        this.showSpeechBubble =
          this.accommodations.length === 0 && this.tours.length === 0;
      } catch (error) {
        console.error('Failed to fetch calendar products data:', error);
        this.error = error.message;
      } finally {
        this.isLoading = false;
      }
    },

    async fetchGridData() {
      this.isLoading = true;
      console.log(
        'fetchGridData called, isNavigatingDates:',
        this.isNavigatingDates,
        'WebSocket state:',
        this.bookingSocket?.readyState,
        'daysView:',
        this.daysView,
      );
      const params = new URLSearchParams();
      if (this.currentDate) {
        params.append('date', this.currentDate);
      }
      params.append('days_view', this.daysView);
      const queryString = params.toString();
      const gridUrl = `/calendar/load-calendar-grid/?${queryString}`;
      const itemsUrl = `/calendar/load-calendar-items/?${queryString}`;

      try {
        // Fetch both endpoints in parallel for better performance
        const [gridResponse, itemsResponse] = await Promise.all([
          fetch(gridUrl, {
            headers: {
              'Content-Type': 'application/json',
              'X-CSRFToken': getCSRFToken(),
              'X-Requested-With': 'XMLHttpRequest',
            },
          }),
          fetch(itemsUrl, {
            headers: {
              'Content-Type': 'application/json',
              'X-CSRFToken': getCSRFToken(),
              'X-Requested-With': 'XMLHttpRequest',
            },
          }),
        ]);

        // Parse both responses in parallel
        const [gridData, itemsData] = await Promise.all([
          safeJsonParse(gridResponse),
          safeJsonParse(itemsResponse),
        ]);

        // Update grid data
        this.calendar_selected = gridData.calendar_selected;
        this.currentDate = gridData.selected_date;
        this.dates = gridData.dates;
        this.calendar_dates = gridData.calendar_dates;
        this.accommodations = gridData.accommodations;
        this.tours = gridData.tours;
        this.calendarDatesWithRates = gridData.calendar_dates_with_rates;
        this.calendarDatesBooked = gridData.calendar_dates_booked;
        this.calendarDatesBlocked = gridData.calendar_dates_blocked || {};
        this.productDatesRateSetBlocked =
          gridData.product_dates_rateset_blocked || {};
        this.productDatesRateSetBlockedNames =
          gridData.product_dates_rateset_blocked_names || {};
        this.showRatesetBlockedBadge =
          gridData.show_rateset_blocked_badge === true;
        this.showPartnerOnCalendarBookingItems =
          gridData.show_partner_on_calendar_booking_items === true;
        // Sync days view from server response
        if (gridData.days_view) {
          this.daysView = gridData.days_view;
        }

        // Load collapsed products state from the response
        this.collapsedProducts = gridData.collapsed_products || {};
        console.log('Loaded collapsed products:', this.collapsedProducts);

        // OPTIMIZED: Load precomputed occupation data from backend
        this.productOccupationSummary =
          gridData.product_occupation_summary || {};
        console.log('Loaded precomputed occupation summary');

        // Update items data
        this.applyCalendarItemsData(itemsData);

        // Set showSpeechBubble based on the existence of accommodations and tours
        this.showSpeechBubble =
          this.accommodations.length === 0 && this.tours.length === 0;

        // Only setup WebSocket if it doesn't exist or is not connected
        if (
          !this.bookingSocket ||
          this.bookingSocket.readyState !== WebSocket.OPEN
        ) {
          console.log('Setting up WebSocket from fetchGridData');
          this.setupWebSocket();
        } else {
          console.log(
            'WebSocket already connected, skipping setup in fetchGridData',
          );
        }

        //console.log('calendarDatesWithRates:', this.calendarDatesWithRates);
        //console.log('calendarDatesBooked:', this.calendarDatesBooked);
      } catch (error) {
        console.error('Failed to fetch calendar data:', error);
        this.error = error.message;
      } finally {
        this.isLoading = false;
        this.scheduleFormShellPreload();
      }
    },
    scheduleFormShellPreload() {
      const calendarId =
        this.calendar_selected?.id ||
        this.calendar_selected ||
        document
          .querySelector('.calendar-container[data-calendar-id]')
          ?.getAttribute('data-calendar-id');

      if (!calendarId) {
        return;
      }

      const calendarIdStr = String(calendarId);
      if (this._formShellsPreloadedForCalendarId === calendarIdStr) {
        return;
      }

      if (this._formShellPreloadTimer) {
        clearTimeout(this._formShellPreloadTimer);
        this._formShellPreloadTimer = null;
      }

      const runPreload = () => {
        this._formShellPreloadTimer = null;
        if (this._formShellsPreloadedForCalendarId === calendarIdStr) {
          return;
        }
        this._formShellsPreloadedForCalendarId = calendarIdStr;
        Alpine.store('addBookingForm')?.preloadFormShells?.(calendarIdStr);
        Alpine.store('editBookingItemForm')?.preloadFormShells?.(calendarIdStr);
      };

      const scheduleAfterIdle = (callback) => {
        if (typeof window.requestIdleCallback === 'function') {
          window.requestIdleCallback(() => callback(), { timeout: 500 });
          return;
        }
        this._formShellPreloadTimer = setTimeout(callback, 300);
      };

      scheduleAfterIdle(runPreload);
    },
    setupWebSocket() {
      if (!this.calendar_selected) {
        console.error('No calendar ID selected');
        return;
      }

      // If we already have an open connection, don't create a new one
      if (
        this.bookingSocket &&
        this.bookingSocket.readyState === WebSocket.OPEN
      ) {
        console.log('WebSocket already connected, skipping setup');
        return;
      }

      // Clear existing ping interval if any
      this.clearPingInterval();

      // Close existing connection if any (but not if it's already open)
      if (
        this.bookingSocket &&
        (this.bookingSocket.readyState === WebSocket.CONNECTING ||
          this.bookingSocket.readyState === WebSocket.CLOSING)
      ) {
        console.log('Closing existing WebSocket connection');
        this.bookingSocket.close(1000, 'Reconnecting');
        this.bookingSocket = null;
      }

      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const host = window.location.host;
      const wsUrl = `${protocol}//${host}/ws/bookings/${this.calendar_selected}/`;

      const MAX_RECONNECT_ATTEMPTS = 5;

      try {
        console.log('Creating new WebSocket connection to:', wsUrl);
        this.bookingSocket = new WebSocket(wsUrl);

        this.bookingSocket.onopen = () => {
          console.log('WebSocket connected');
          this.reconnectAttempts = 0;
          this.startPingInterval();
        };

        this.bookingSocket.onmessage = async (e) => {
          try {
            const data = JSON.parse(e.data);

            // Handle connection established message
            if (data.type === 'connection_established') {
              console.log('Connection established:', data.message);
              return;
            }

            // Handle error messages
            if (data.type === 'error') {
              console.error('WebSocket error:', data.message);
              if (this.toastStore && this.toastStore.addToast) {
                this.toastStore.addToast(data.message, 'error');
              }
              return;
            }

            // Handle pong responses with timestamp
            if (data.type === 'pong') {
              const latency = Date.now() - data.timestamp;
              console.debug('WebSocket latency:', latency, 'ms');
              return;
            }

            // Handle other messages
            switch (data.event) {
              case 'new_booking':
              case 'update_booking':
                // Skip WebSocket update if we just updated locally to prevent flickering
                if (this.skipNextWebSocketUpdate) {
                  console.log(
                    'Skipping WebSocket update to prevent flickering',
                  );
                  this.skipNextWebSocketUpdate = false;
                  this.toastStore.addToast(
                    `Booking for ${data.first_name} ${
                      data.last_name
                    } has been ${
                      data.event === 'new_booking' ? 'added' : 'updated'
                    }!`,
                    'success',
                  );
                } else {
                  // Only fetch if not currently loading to prevent duplicate requests
                  if (!this.isLoading) {
                    this.isLoading = true;
                    try {
                      await this.fetchItemsData();
                      this.toastStore.addToast(
                        `Booking for ${data.first_name} ${
                          data.last_name
                        } has been ${
                          data.event === 'new_booking' ? 'added' : 'updated'
                        }!`,
                        'success',
                      );
                    } catch (error) {
                      console.error(
                        'Error fetching items after WebSocket update:',
                        error,
                      );
                    } finally {
                      this.isLoading = false;
                    }
                  } else {
                    console.log('Skipping fetch - already loading');
                  }
                }
                break;

              case 'new_booking_ota':
                // OTA booking created (from Channex webhook)
                if (!this.isLoading) {
                  this.isLoading = true;
                  try {
                    await this.fetchItemsData();
                    const platformName = data.booking_source
                      ? data.booking_source
                          .replace('_', '.')
                          .toLowerCase()
                          .split('.')
                          .map(
                            (word) =>
                              word.charAt(0).toUpperCase() + word.slice(1),
                          )
                          .join('.')
                      : 'Platform';
                    this.toastStore.addToast(
                      `New booking from ${platformName}! ${data.first_name} ${data.last_name}`,
                      'success',
                    );
                  } catch (error) {
                    console.error(
                      'Error fetching items after OTA booking:',
                      error,
                    );
                  } finally {
                    this.isLoading = false;
                  }
                }
                break;

              case 'booking_modified_ota':
                // OTA booking modified (from Channex webhook)
                if (!this.isLoading) {
                  this.isLoading = true;
                  try {
                    await this.fetchItemsData();
                    const platformName = data.booking_source
                      ? data.booking_source
                          .replace('_', '.')
                          .toLowerCase()
                          .split('.')
                          .map(
                            (word) =>
                              word.charAt(0).toUpperCase() + word.slice(1),
                          )
                          .join('.')
                      : 'Platform';
                    this.toastStore.addToast(
                      `Booking modified on ${platformName}: ${data.first_name} ${data.last_name}`,
                      'info',
                    );
                  } catch (error) {
                    console.error(
                      'Error fetching items after booking modification:',
                      error,
                    );
                  } finally {
                    this.isLoading = false;
                  }
                }
                break;

              case 'booking_cancelled_ota':
                // OTA booking cancelled (from Channex webhook)
                if (!this.isLoading) {
                  this.isLoading = true;
                  try {
                    await this.fetchItemsData();
                    const platformName = data.booking_source
                      ? data.booking_source
                          .replace('_', '.')
                          .toLowerCase()
                          .split('.')
                          .map(
                            (word) =>
                              word.charAt(0).toUpperCase() + word.slice(1),
                          )
                          .join('.')
                      : 'Platform';
                    this.toastStore.addToast(
                      `Booking cancelled on ${platformName}: ${data.first_name} ${data.last_name}`,
                      'warning',
                    );
                  } catch (error) {
                    console.error(
                      'Error fetching items after booking cancellation:',
                      error,
                    );
                  } finally {
                    this.isLoading = false;
                  }
                }
                break;
              case 'new_holding_booking':
                // Only fetch if not currently loading to prevent duplicate requests
                if (!this.isLoading) {
                  this.isLoading = true;
                  try {
                    await this.fetchItemsData();
                    this.toastStore.addToast(
                      'Someone added to their cart on your Online Store!',
                      'success',
                    );
                  } catch (error) {
                    console.error(
                      'Error fetching items after WebSocket update:',
                      error,
                    );
                  } finally {
                    this.isLoading = false;
                  }
                } else {
                  console.log('Skipping fetch - already loading');
                }
                break;
              case 'new_update_block':
              case 'delete_booking_item':
              case 'expire_session':
                // Only fetch if not currently loading to prevent duplicate requests
                if (!this.isLoading) {
                  this.isLoading = true;
                  try {
                    await this.fetchItemsData();
                  } catch (error) {
                    console.error(
                      'Error fetching items after WebSocket update:',
                      error,
                    );
                  } finally {
                    this.isLoading = false;
                  }
                } else {
                  console.log('Skipping fetch - already loading');
                }
                break;
              default:
                console.log('Unknown event type:', data.event);
            }
          } catch (error) {
            console.error('Error handling WebSocket message:', error);
          }
        };

        this.bookingSocket.onclose = (e) => {
          console.log(
            'WebSocket closed with code:',
            e.code,
            'reason:',
            e.reason,
          );
          this.clearPingInterval();

          // Handle specific close codes
          if (e.code === 4029) {
            console.error('Rate limit exceeded');
            this.toastStore.addToast(
              'Too many connections. Please try again later.',
              'error',
            );
            return;
          }

          // Don't reconnect if it was a clean closure or intentional reconnection
          if (
            e.code === 1000 ||
            e.code === 1001 ||
            e.reason === 'Reconnecting'
          ) {
            console.log('Clean WebSocket closure, not reconnecting');
            return;
          }

          // Only attempt to reconnect for unexpected closures
          if (this.reconnectAttempts < MAX_RECONNECT_ATTEMPTS) {
            this.reconnectAttempts++;
            const delay = Math.min(
              1000 * Math.pow(2, this.reconnectAttempts),
              30000,
            );
            console.log(
              `WebSocket reconnection attempt ${this.reconnectAttempts} in ${delay}ms`,
            );
            setTimeout(() => {
              // Only reconnect if we don't already have a connection
              if (
                !this.bookingSocket ||
                this.bookingSocket.readyState !== WebSocket.OPEN
              ) {
                this.setupWebSocket();
              }
            }, delay);
          } else {
            console.error('Max reconnection attempts reached');
            this.toastStore.addToast(
              'Unable to maintain connection. Please refresh the page.',
              'error',
            );
          }
        };

        this.bookingSocket.onerror = (error) => {
          console.error('WebSocket error:', error);
          this.clearPingInterval();
          this.toastStore.addToast('Connection error occurred.', 'error');
        };
      } catch (error) {
        console.error('Error setting up WebSocket:', error);
        this.clearPingInterval();
        this.toastStore.addToast('Failed to establish connection.', 'error');
      }
    },

    startPingInterval() {
      this.pingInterval = setInterval(() => {
        if (this.bookingSocket?.readyState === WebSocket.OPEN) {
          this.bookingSocket.send(
            JSON.stringify({
              type: 'ping',
              timestamp: Date.now(), // Add timestamp for latency calculation
            }),
          );
        }
      }, 30000); // Send ping every 30 seconds
    },

    clearPingInterval() {
      try {
        if (this.pingInterval) {
          clearInterval(this.pingInterval);
          this.pingInterval = null;
        }
      } catch (error) {
        console.debug('Clear ping interval error:', error);
      }
    },

    // Add cleanup method
    cleanup() {
      try {
        if (this.pingInterval) {
          clearInterval(this.pingInterval);
          this.pingInterval = null;
        }
        if (
          this.bookingSocket &&
          this.bookingSocket.readyState === WebSocket.OPEN
        ) {
          this.bookingSocket.close(1000, 'Page unloading');
          this.bookingSocket = null;
        }
        if (this.flatpickrInstance) {
          this.flatpickrInstance.destroy();
          this.flatpickrInstance = null;
        }
      } catch (error) {
        console.debug('Cleanup error:', error);
      }
    },

    initializeFlatpickr(element) {
      console.log('Initializing Flatpickr for calendar navigation');

      // Destroy existing instance if it exists
      if (this.flatpickrInstance) {
        this.flatpickrInstance.destroy();
      }

      this.flatpickrInstance = flatpickr(element, {
        dateFormat: 'd/M/Y',
        defaultDate: this.currentDate,
        allowInput: false,
        clickOpens: true,
        onOpen: (selectedDates, dateStr, instance) => {
          // Always navigate to the month of the current date when opening
          if (this.currentDate) {
            const currentDateObj = this.parseLocalDate(this.currentDate);
            instance.jumpToDate(currentDateObj);
            // Preselect the current date
            instance.setDate(currentDateObj, false); // false prevents triggering onChange
          }
        },
        onChange: (selectedDates, dateStr) => {
          if (selectedDates.length > 0) {
            this.isNavigatingDates = true;
            // Convert the selected date back to Y-m-d format for internal use
            const selectedDate = selectedDates[0];
            const isoDate = formatDateISO(selectedDate);
            this.currentDate = isoDate;
            this.fetchGridData().finally(() => {
              this.isNavigatingDates = false;
            });
          }
        },
      });

      return this.flatpickrInstance;
    },
    // Not currently used
    async moveDate(days) {
      if (this.isFetching) return; // Prevent further execution if already fetching
      this.isFetching = true; // Set fetching state to true
      this.isNavigatingDates = true;
      let current = this.currentDate
        ? this.parseLocalDate(this.currentDate)
        : new Date();
      current.setDate(current.getDate() + days);
      const year = current.getFullYear();
      const month = String(current.getMonth() + 1).padStart(2, '0');
      const day = String(current.getDate()).padStart(2, '0');
      this.currentDate = `${year}-${month}-${day}`;
      // Update Flatpickr instance if it exists
      if (this.flatpickrInstance) {
        this.flatpickrInstance.setDate(this.currentDate, false); // false prevents triggering onChange
      }
      await this.fetchGridData(); // Await fetchGridData to complete
      this.isFetching = false; // Reset fetching state after completion
      this.isNavigatingDates = false;
    },
    goToDate(date) {
      this.isNavigatingDates = true;
      this.currentDate = date;
      // Update Flatpickr instance if it exists
      if (this.flatpickrInstance) {
        this.flatpickrInstance.setDate(date, false); // false prevents triggering onChange
      }
      this.fetchGridData().finally(() => {
        this.isNavigatingDates = false;
      });
    },
    goToToday() {
      const today = new Date();
      const todayStr =
        today.getFullYear() +
        '-' +
        String(today.getMonth() + 1).padStart(2, '0') +
        '-' +
        String(today.getDate()).padStart(2, '0');
      this.goToDate(todayStr);
    },
    handleKeydown(event) {
      if (event.keyCode === 37) {
        // Left arrow
        this.moveDate(-7);
      } else if (event.keyCode === 39) {
        // Right arrow
        this.moveDate(7);
      }
    },

    getCheckinStatusLabel(bookingItem, statusKey) {
      // Use custom labels from the booking if available
      if (
        bookingItem.booking &&
        bookingItem.booking.checkin_status_labels &&
        bookingItem.booking.checkin_status_labels[statusKey]
      ) {
        return bookingItem.booking.checkin_status_labels[statusKey];
      }

      // Fallback to hardcoded mapping
      const statusMap = {
        '': 'Not Set',
        CHECKED_IN: 'Checked In',
        CHECKED_OUT: 'Checked Out',
        NO_SHOW: 'No Show',
      };
      return statusMap[statusKey] || 'Not Set';
    },

    getProductByUnitId(item) {
      if (!item) return null;

      if (item.accommodation_unit) {
        return this.accommodations.find((accommodation) =>
          (accommodation.accommodation_units || []).some(
            (unit) => unit.id === item.accommodation_unit,
          ),
        );
      }

      if (item.tour_unit) {
        return this.tours.find((tour) =>
          (tour.tour_units || []).some((unit) => unit.id === item.tour_unit),
        );
      }

      return null;
    },

    // Get display name for booking item based on current days view
    // For 10-day view: show more of the name since we have wider cells
    // For 25-day view: use the pre-computed abbreviated formatted_name
    applyCalendarItemsData(data) {
      if (!data) {
        return;
      }
      this.bookingItems = data.booking_items;
      this.blockItems = data.block_items;
      if (data.show_partner_on_calendar_booking_items !== undefined) {
        this.showPartnerOnCalendarBookingItems =
          data.show_partner_on_calendar_booking_items === true;
      }
      // Clear filter cache when items are updated
      this._bookingItemsCache = null;
      this._blockItemsCache = null;
      if (data.calendar_dates_booked) {
        this.calendarDatesBooked = {
          ...this.calendarDatesBooked,
          ...data.calendar_dates_booked,
        };
      }
      const cartStore = Alpine.store('cartStore');
      if (cartStore) {
        cartStore.editModeBooking = data.edit_mode_booking;
        cartStore.bookingItemsCount = data.booking_items_count;
      }
    },
    getBookingPartner(item) {
      return item?.booking?.partner || null;
    },
    shouldShowBookingPartnerBadge(bookingWrapper) {
      const item = bookingWrapper?.item ?? bookingWrapper;
      return (
        this.showPartnerOnCalendarBookingItems && !!this.getBookingPartner(item)
      );
    },
    getPartnerIconUrlForItem(item) {
      return getPartnerIconUrl(this.getBookingPartner(item));
    },
    getPartnerInitialForItem(item) {
      return getPartnerInitial(this.getBookingPartner(item));
    },
    getPartnerTooltip(item) {
      return this.getBookingPartner(item)?.name || '';
    },
    getPartnerBadgeStyleForItem(item) {
      return getPartnerBadgeStyle(this.getBookingPartner(item));
    },
    partnerIconNeedsContainFitForItem(item) {
      return partnerIconNeedsContainFit(this.getBookingPartner(item));
    },
    getBookingDisplayName(item) {
      if (!item) return '';
      const product = this.getProductByUnitId(item);
      const showRateSetName = !!product?.show_rateset_name_on_calendar;

      if (showRateSetName) {
        return item.rate_set?.public_name || item.formatted_name || '';
      }

      // For 10-day view, show fuller name if available
      if (this.daysView === 10 && item.booking?.main_guest) {
        const firstName = item.booking.main_guest.first_name || '';
        const lastName = item.booking.main_guest.last_name || '';
        const lastInitial = lastName ? lastName[0].toUpperCase() : '';

        // Show full first name + last initial for 10-day view
        if (firstName) {
          return `${firstName} ${lastInitial}`.trim();
        }
      }

      // Default to the pre-computed formatted_name (abbreviated for 25-day view)
      return item.formatted_name || '';
    },

    // Get display name for block item based on current days view
    getBlockDisplayName(item) {
      if (!item) return '';

      // For 10-day view, show more of the block name
      if (this.daysView === 10 && item.name) {
        return item.name;
      }

      // Default to the pre-computed formatted_name
      return item.formatted_name || item.name || '';
    },

    // Check if a booking is "past" (current date is 1+ day after the booking's end date)
    // This is used to apply muted colors to past bookings
    isBookingPast(endDateStr) {
      if (!endDateStr) return false;
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const endDate = this.parseLocalDate(endDateStr);
      if (!endDate) return false;
      // Add 1 day to end date to get the day after checkout
      const dayAfterCheckout = new Date(endDate);
      dayAfterCheckout.setDate(dayAfterCheckout.getDate() + 1);
      // Return true if today is >= day after checkout
      return today >= dayAfterCheckout;
    },

    // Save collapsed products state to backend
    async saveCollapsedProducts() {
      if (!this.calendar_selected) return;

      try {
        const response = await fetch('/calendar/update-collapsed-products/', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': getCSRFToken(),
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify({
            calendar_id: this.calendar_selected,
            collapsed_products: this.collapsedProducts,
          }),
        });

        // Check for auth errors (403 or HTML response)
        if (await checkAuthError(response)) {
          handleAuthError();
          return;
        }

        if (!response.ok) {
          console.error('Failed to save collapsed products');
        }
      } catch (error) {
        // If it's an auth error, it's already handled
        if (error.message === 'Authentication required') {
          return;
        }
        console.error('Failed to save collapsed products:', error);
      }
    },

    // Toggle product collapse/expand state
    toggleProductCollapse(productId) {
      this.collapsedProducts[productId] = !this.collapsedProducts[productId];
      // Save to backend
      this.saveCollapsedProducts();
    },

    // Check if product is collapsed
    isProductCollapsed(productId) {
      return this.collapsedProducts[productId] || false;
    },

    // OPTIMIZED: Get occupation count for each date for a product
    // Now uses precomputed data from backend instead of calculating on frontend
    getProductOccupationCounts(product, productType) {
      // Return precomputed data from backend if available
      if (
        this.productOccupationSummary &&
        this.productOccupationSummary[product.id]
      ) {
        return this.productOccupationSummary[product.id];
      }

      // Fallback to empty object if precomputed data not available
      console.warn(`No precomputed occupation data for product ${product.id}`);
      return {};
    },
    isProductDateBlockedByRateSet(product, date) {
      if (!this.showRatesetBlockedBadge || !product?.id || !date) {
        return false;
      }
      const productId = String(product.id);
      return this.productDatesRateSetBlocked?.[productId]?.[date] === true;
    },
    getBlockedListingsTooltip(product, date) {
      if (!this.isProductDateBlockedByRateSet(product, date)) {
        return '';
      }
      const productId = String(product.id);
      const blockedNames =
        this.productDatesRateSetBlockedNames?.[productId]?.[date] || [];
      if (!Array.isArray(blockedNames) || blockedNames.length === 0) {
        return '';
      }
      return `Blocked<br/>&bull; ${blockedNames.join('<br/>&bull; ')}`;
    },

    // Reorder products (move up or down)
    async reorderProduct(productId, productType, direction) {
      try {
        const response = await fetch('/products/reorder-product/', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': getCSRFToken(),
          },
          body: JSON.stringify({
            product_id: productId,
            product_type: productType,
            direction: direction,
          }),
        });

        const data = await response.json();

        if (data.success) {
          // Reload the calendar grid to get the updated order
          await this.fetchGridData();

          // Show success toast
          if (this.toastStore) {
            const productTypeName =
              productType === 'accommodation' ? 'Accommodation' : 'Tour';
            this.toastStore.addToast(
              `${productTypeName} moved ${direction}`,
              'success',
            );
          }
        } else {
          // Show error toast
          if (this.toastStore) {
            const productTypeName =
              productType === 'accommodation' ? 'accommodation' : 'tour';
            this.toastStore.addToast(
              data.error || `Failed to reorder ${productTypeName}`,
              'danger',
            );
          }
        }
      } catch (error) {
        console.error('Error reordering product:', error);
        if (this.toastStore) {
          this.toastStore.addToast(
            'An error occurred while reordering the product',
            'danger',
          );
        }
      }
    },
  });

  return store;
}
